import { Box } from '@mui/material';
import type { ReactNode } from 'react';

import { MaterialSymbol } from '@/components/MaterialSymbol';

export type NoticeTone = 'warning' | 'coral' | 'success' | 'info';

const TONES: Record<NoticeTone, { bg: string; fg: string; icon: string }> = {
  warning: {
    bg: 'var(--color-warning-10)',
    fg: 'var(--color-warning-100)',
    icon: 'var(--color-warning-80)',
  },
  coral: {
    bg: 'var(--color-coral-10)',
    fg: 'var(--color-coral-100)',
    icon: 'var(--color-coral-80)',
  },
  success: {
    bg: 'var(--color-success-10)',
    fg: 'var(--color-success-100)',
    icon: 'var(--color-success-80)',
  },
  // Nothing wrong, just a note — the empty rails' "what will be here" (3 Nov).
  info: {
    bg: 'var(--color-blue-10)',
    fg: 'var(--color-blue-100)',
    icon: 'var(--color-blue-80)',
  },
};

export type NoticeChipProps = {
  tone: NoticeTone;
  /** Material Symbols glyph name — `info`, `warning`, `check_circle`. */
  icon: string;
  children: ReactNode;
  automationId?: string;
  /** Echoed as `data-state`, for callers whose tests read which state shows. */
  state?: string;
};

/**
 * A quiet tinted line: small icon, 11px text. First drawn inline as the
 * Financials Contract summary's CALC-14 diff chip ("450.000 DKK er endnu
 * ikke fordelt på kategorier"), lifted here on 2026-10-20 when the designer
 * picked it for the Resources popover's over-budget warning as well — so the
 * two read the same and cannot drift.
 */
export const NoticeChip = ({ tone, icon, children, automationId, state }: NoticeChipProps) => {
  const colors = TONES[tone];
  return (
    <Box
      data-automation-id={automationId}
      data-state={state}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1,
        py: 1,
        borderRadius: 'var(--border-radius-small)',
        bgcolor: colors.bg,
      }}
    >
      <MaterialSymbol name={icon} size={14} color={colors.icon} />
      <Box component="span" sx={{ fontSize: 11, color: colors.fg, lineHeight: 1.35 }}>
        {children}
      </Box>
    </Box>
  );
};
